import { countMenuBadges } from '@/services/menu-badge';
import { useCallback, useEffect, useRef, useState } from 'react';

/** 轮询间隔：设计 10-03-16 4.8 定的 60 秒，改这里不用动后端 */
const REFRESH_MS = 60_000;

/**
 * 菜单待办角标（API-10-200）。
 *
 * 取数时机按设计 4.8：进后台取一次 → 每 60 秒轮询（标签页不可见时跳过，回到前台补一次）
 * → 切页时补一次（`refreshKey` 由布局层传当前路径，本 hook 不依赖路由上下文）。
 * 失败保留上一次的数字、不弹提示——角标是壳层的装饰，不能挡页面。
 *
 * 返回值是「菜单键 → 待办数」；层级之和由调用方按菜单树自己加，本 hook 不做聚合。
 */
export default function useMenuBadges(
  refreshKey?: string,
): Record<string, number> {
  const [badges, setBadges] = useState<Record<string, number>>({});
  /** 只认最后一次请求的结果：切页与轮询撞车时，晚到的旧响应不许覆盖新值 */
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const resp = await countMenuBadges({ skipErrorHandler: true });
      if (mine !== seq.current) return;
      setBadges(
        Object.fromEntries(resp.counts.map((row) => [row.key, row.count])),
      );
    } catch {
      // 取不到就保留上一次：PRD 原文「停在旧值上不报错、只是数字不跟」
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      void load();
    }, REFRESH_MS);
    const onVisibilityChange = () => {
      if (!document.hidden) void load();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    const onRefresh = () => {
      void load();
    };
    window.addEventListener('muou:menu-badges-refresh', onRefresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('muou:menu-badges-refresh', onRefresh);
    };
  }, [load]);

  return badges;
}
